import { prisma } from "@marble/db/client";
import type { State, SuperviseePaymentTiming } from "@prisma/client";

/**
 * Whether a state is already live (`StateConfiguration.active`). Used to
 * pick the "start seeing students" vs. "get ready for our launch" copy in
 * the therapist welcome email.
 *
 * Deliberately narrower than the app's own `getStateConfig`
 * (`apps/web/src/lib/modules/states/queries.ts`): that query hydrates the
 * full `StateConfiguration` row (supervision pay defaults, DocuSign
 * templates, Redis caching with a versioned cache key, a large
 * `DEFAULT_DISABLED_STATE_CONFIG` fallback, ...) — none of which this needs,
 * and porting that whole module into `@marble/core` for one boolean would be
 * a disproportionate pull of unrelated app-only domain into core. This reads
 * the same column directly, uncached, defaulting to `false` (not launched)
 * when no row exists — the same default the app's config carries.
 */
export async function isStateLaunchActive(state: State): Promise<boolean> {
  const config = await prisma.stateConfiguration.findFirst({
    where: { state },
    select: { active: true },
  });
  return config?.active ?? false;
}

/**
 * All active states. Same narrowing rationale as {@link isStateLaunchActive}:
 * the app's own `getActiveStates` lives beside the full `StateConfiguration`
 * module, which this package doesn't need just for the state list.
 */
export async function listActiveStates(): Promise<State[]> {
  const rows = await prisma.stateConfiguration.findMany({
    where: { active: true },
    select: { state: true },
  });
  return rows.map((row) => row.state);
}

/**
 * The states whose supervisee session pay follows `timing`: paid at
 * finalization, or held until the supervisor signs off. Same narrowing
 * rationale as {@link isStateLaunchActive}.
 */
export async function listStatesWithSuperviseePaymentTiming(
  timing: SuperviseePaymentTiming,
): Promise<State[]> {
  const rows = await prisma.stateConfiguration.findMany({
    where: { superviseePaymentTiming: timing },
    select: { state: true },
  });
  return rows.map((row) => row.state);
}
